import { PropsWithChildren, ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import Spinner from "@brighthr/component-spinner";
import { DestinationState } from "shared/core/state/destinationState";
import "shared/extensions/string";
import { UserState } from "../../../state/userState";
import { useUserContext } from "../../../state/UserContext/UserContext";
import { UseAuthResult } from "core/auth/model";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { useAuthRefresh } from "core/auth/version2/useAuth/useAuthRefresh";
import { isExpiring } from "core/helpers/dates";
import { UserAuthState } from "shared/core/state/userAuthState";
import { DailyActiveUsageLogger } from "../DailyActiveUsageLogger/DailyActiveUsageLogger";

interface ProtectedRouteProps extends PropsWithChildren {
  auth: UseAuthResult;
  allowRefresh?: boolean;
  handleShouldAuthenticate?: Function;
}

export const ProtectedRoute = ({ children, auth, handleShouldAuthenticate, allowRefresh }: ProtectedRouteProps): ReactElement => {
  const { t } = useTranslation();
  const { isAdditionalInfoComplete } = useUserContext();
  const location = useLocation();
  const { isLoading } = useAuthRefresh(!!allowRefresh && isExpiring(UserAuthState.get()?.expires_at));

  if (auth.isLoading || isLoading) {
    return <Spinner className="stroke-brand-purple" ariaLabel={t("login.inProgressMessage")} />;
  }

  if (auth.shouldAuthenticate) {
    DestinationState.set(location.pathname + location.hash);

    if (handleShouldAuthenticate) {
      handleShouldAuthenticate();
    } else if (ClientInfoState.getCode()) {
      auth.signinRedirect({ extraQueryParams: { client_code: ClientInfoState.getCode() } });
    } else {
      return <Navigate to="/login" />;
    }
  }

  if (!auth.isAuthenticated) {
    return <ErrorAlert title={t("login.error.title")} content="" />;
  }

  if (UserState.get() === undefined) {
    DestinationState.set(location.pathname + location.hash);
    return <Navigate to="/auth" />;
  }

  if (!isAdditionalInfoComplete() && location.pathname !== "/additional-info") {
    DestinationState.set(location.pathname + location.hash);
    return <Navigate to="/additional-info" />;
  }

  return (
    <>
      <DailyActiveUsageLogger />
      {children}
    </>
  );
};
